import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { connect } from 'react-redux';

import { TerrainInfoComp, CustomModal } from './Helpers';
import { getToastMsg } from '../../../ErrorMessage';

import { TERRAIN_INFO_KEYS } from '../../constants';
import { removeEvent, addEvent } from '../../../../lib/utils';
import api from '../../../../lib/api';
import endpoints from '../../../../lib/endpoints';
import {
  closeEditor,
  closeOverviewModal,
} from '../../../../redux/actions/trekOverviewActions';
import {
  patchOverviewEdits,
  updateOverviewEdits,
} from '../../../../redux/actions/overviewEditorActions';
import { openLightbox } from '../../../../redux/actions/lightboxStateActions';

import SmallLoadingWrapper from '../../../Loading/SmallLoadingWrapper';

const initalModalData = {
  modalMsg: '',
  modal: false,
  modalType: '',
};

type Props = {
  terrain_information: {
    difficulty_level: string,
    highest_altitude: {
      feet: null,
      meters: string,
    },
    total_length: {
      km: string,
      miles: null,
    },
    // UNNECESARY KEY
    total_climb: null,
    average_walking_hrs: null,
    has_domestic_air_travel: null,
  },
  quick_facts: {
    permits: Array<String>,
    start_location: string,
    finish_location: string,
    best_seasons: Array<string>,
    // UNNECESSARY KEYS
    best_season: null,
  },
  isLightBoxOpen: boolean,
  renderedAsModal: boolean,
  elevation_profile: string | null,
  closeEditor: () => void,
  closeOverviewModal: () => void,
  updateOverviewEdits: () => void,
};

type State = {
  modalMsg: string,
  modal: boolean,
  modalType: string,
  loading: boolean,
  simpleIpValue: Object,
};

class TerrainInfoEditor extends Component<Props, State> {
  state = {
    simpleIpValue: {},
    loading: false,
    ...initalModalData,
  };

  componentDidMount() {
    const { terrain_information, quick_facts, elevation_profile } = this.props;

    // DELETE UNNECESSARY KEYS TO AVOID ERROR WHILE PATCHING DATA
    if (isObject(quick_facts)) delete quick_facts.best_season;
    if (isObject(terrain_information)) {
      delete terrain_information.total_climb;
      delete terrain_information.average_walking_hrs;
      delete terrain_information.has_domestic_air_travel;
    }

    const combinedData = {
      ...terrain_information,
      ...quick_facts,
      elevation_profile,
    };

    addEvent({
      elem: window,
      type: 'click',
      eventHandle: this.onOutsideEditorClick,
      options: true,
    });

    let modifiedSimpleIpValue = { ...combinedData };

    if (!isEmpty(combinedData.total_length))
      modifiedSimpleIpValue = {
        ...modifiedSimpleIpValue,
        total_length: modifiedSimpleIpValue.total_length.km,
      };

    if (!isEmpty(combinedData.highest_altitude))
      modifiedSimpleIpValue = {
        ...modifiedSimpleIpValue,
        highest_altitude: modifiedSimpleIpValue.highest_altitude.meters,
      };

    if (!isEmpty(combinedData.best_seasons)) {
      modifiedSimpleIpValue = {
        ...modifiedSimpleIpValue,
        best_seasons: combinedData.best_seasons.join(),
      };
    }

    if (!isEmpty(combinedData.permits)) {
      modifiedSimpleIpValue = {
        ...modifiedSimpleIpValue,
        permits: combinedData.permits.join(),
      };
    }

    this.setState({
      simpleIpValue: modifiedSimpleIpValue,
    });
  }

  componentWillUnmount() {
    removeEvent({
      elem: window,
      type: 'click',
      eventHandle: this.onOutsideEditorClick,
      options: true,
    });
  }

  onOutsideEditorClick = e => {
    const { isLightBoxOpen, closeEditor, renderedAsModal, closeOverviewModal } = this.props;
    if (!this.state.modal && !isLightBoxOpen) {
      if (!document.getElementById('trek_quick_fact').contains(e.target)) {
        if (this.hasInputChanged()) {
          this.setState({
            modal: true,
            modalType: 'notEmptyData',
            modalMsg: 'You have unsaved data. Would you like to save them?',
          });
        } else {
          if (renderedAsModal) {
            closeOverviewModal();
          } else {
            closeEditor();
          }
          this.clearModalData();
        }
      }
    }
  };

  clearModalData = () =>
    this.setState({
      ...initalModalData,
    });

  hasQuickFactsChanged = () =>
    !isEqual(this.modifiedInputData().quickFactData, this.modifiedSaveDate().quickFactData);

  hasElevationProfileChanged = () =>
    !(this.modifiedInputData().elevationData === this.modifiedSaveDate().elevationData);

  hasInputChanged = () => !isEqual(this.modifiedInputData(), this.modifiedSaveDate());

  modifiedInputData = () => {
    const { terrain_information, quick_facts, elevation_profile } = this.props;
    const combinedData = terrain_information
      ? quick_facts
        ? {
            terrain_information,
            quick_facts,
          }
        : { terrain_information }
      : quick_facts
      ? { quick_facts }
      : {};

    return { quickFactData: combinedData, elevationData: elevation_profile };
  };

  hasKey = key => {
    const { terrain_information, quick_facts, elevation_profile } = this.props;
    const combinedData = {
      ...terrain_information,
      ...quick_facts,
      elevation_profile,
    };

    const keysInProps = Object.keys(combinedData);

    return keysInProps.indexOf(key) !== -1;
  };

  modifiedSaveDate = () => {
    const { simpleIpValue } = this.state;
    let data = {};
    let elevationData = null;

    const getRootKey = {
      difficulty_level: 'terrain_information',
      highest_altitude: 'terrain_information',
      total_length: 'terrain_information',
      start_location: 'quick_facts',
      finish_location: 'quick_facts',
      // best_season: 'quick_facts',
      best_seasons: 'quick_facts',
      permits: 'quick_facts',
      total_climb: 'terrain_information',
      average_walking_hrs: 'terrain_information',
      has_domestic_air_travel: 'terrain_information',
    };

    Object.keys(simpleIpValue).forEach(keys => {
      if (simpleIpValue[keys] || this.hasKey(keys)) {
        if (keys === 'elevation_profile') {
          elevationData = simpleIpValue[keys];
        } else if (keys === 'best_season') {
          //do nothing
        } else if (keys === 'permits' || keys === 'best_seasons') {
          data[getRootKey[keys]] = {
            ...data[getRootKey[keys]],
            [keys]:
              simpleIpValue[keys] && simpleIpValue[keys].split(',').filter(val => val),
          };
        } else {
          data[getRootKey[keys]] = {
            ...data[getRootKey[keys]],
            [keys]: simpleIpValue[keys],
          };
        }
      }
    });

    if (data.terrain_information && data.terrain_information.total_length) {
      data = {
        ...data,
        terrain_information: {
          ...data.terrain_information,
          total_length: {
            km: data.terrain_information.total_length,
          },
        },
      };
    } else if (this.hasKey('total_length')) {
      data = {
        ...data,
        terrain_information: {
          ...data.terrain_information,
          total_length: { km: 0 },
        },
      };
    }

    if (data.terrain_information && data.terrain_information.highest_altitude) {
      data = {
        ...data,
        terrain_information: {
          ...data.terrain_information,
          highest_altitude: {
            meters: data.terrain_information.highest_altitude,
          },
        },
      };
    } else if (this.hasKey('highest_altitude')) {
      data = {
        ...data,
        terrain_information: {
          ...data.terrain_information,
          highest_altitude: { meters: 0 },
        },
      };
    }

    if (
      data.terrain_information &&
      data.terrain_information.difficulty_level === 'Select One'
    ) {
      if (this.hasKey('difficulty_level')) {
        data.terrain_information.difficulty_level = '';
      } else {
        delete data.terrain_information.difficulty_level;
      }
    }

    return {
      quickFactData: data,
      elevationData,
    };
  };
  onSimpleIpChange = event => {
    this.setState({
      simpleIpValue: {
        ...this.state.simpleIpValue,
        [event.target.name]: event.target.value,
      },
    });
  };

  onRadioIpChange = value => {
    this.setState({
      simpleIpValue: {
        ...this.state.simpleIpValue,
        has_domestic_air_travel: value,
      },
    });
  };

  closeAndToggle = () => {
    const { closeEditor, renderedAsModal, closeOverviewModal } = this.props;
    this.toggle();
    if (renderedAsModal) {
      closeOverviewModal();
    } else {
      closeEditor();
    }
  };

  toggle = () => {
    this.clearModalData();
  };

  setLoadingState = status => this.setState({ loading: status });

  handleSave = async () => {
    const { patchOverviewEdits, trekId } = this.props;
    const { quickFactData, elevationData } = this.modifiedSaveDate();

    if (this.hasElevationProfileChanged()) {
      try {
        const fd = new FormData();
        fd.append('file', elevationData);
        this.setLoadingState(true);
        const { data } = await api.putFile(endpoints.treks.elevationProfile(trekId), fd);
        this.handlePatchSuccess(data);
      } catch (err) {
        this.handlePatchError();
      }
    }
    if (this.hasQuickFactsChanged()) {
      this.setLoadingState(true);
      patchOverviewEdits(trekId, quickFactData)
        .then(() => {
          this.handlePatchSuccess(quickFactData);
        })
        .catch(() => this.handlePatchError());
    }
  };

  handlePatchSuccess = data => {
    const {
      updateOverviewEdits,
      closeEditor,
      renderedAsModal,
      closeOverviewModal,
    } = this.props;
    updateOverviewEdits(data);
    if (renderedAsModal) {
      closeOverviewModal();
    } else {
      closeEditor();
    }
    this.clearModalData();
    this.setLoadingState(false);
    getToastMsg({ status: 'success', type: 'editQuickFacts' });
  };

  handlePatchError = () => {
    this.setLoadingState(false);
    getToastMsg({ status: 'error', type: 'editQuickFacts' });
  };

  renderSaveButton = () =>
    this.hasInputChanged() ? (
      <div className="save-btn">
        <a onClick={this.handleSave}>
          <i className="icon icon-hg-save" aria-hidden="true" /> Save
        </a>
      </div>
    ) : null;

  render() {
    const { simpleIpValue, modalMsg, modal, modalType, loading } = this.state;

    const { renderedAsModal, closeOverviewModal, openLightbox } = this.props;
    return (
      <div className="white-panel" id="trek_quick_fact">
        {renderedAsModal && (
          <a onClick={closeOverviewModal}>
            <i className="icon icon-hg-chev-left" aria-hidden="true" />
          </a>
        )}
        <h2>QUICK FACTS</h2>
        <SmallLoadingWrapper showLoading={loading}>
          {this.renderSaveButton()}
          <ul className="overview-info-list clearfix">
            {TERRAIN_INFO_KEYS.map(info => (
              <TerrainInfoComp
                key={info.key}
                fieldKey={info.key}
                title={info.title}
                subtitle={info.subtitle}
                simpleIpValue={simpleIpValue[info.key]}
                onSimpleIpChange={this.onSimpleIpChange}
                onRadioIpChange={this.onRadioIpChange}
                openLightbox={openLightbox}
              />
            ))}
          </ul>
        </SmallLoadingWrapper>
        <CustomModal
          modal={modal}
          modalMsg={modalMsg}
          modalType={modalType}
          toggle={this.toggle}
          handleSave={this.handleSave}
          closeAndToggle={this.closeAndToggle}
        />
      </div>
    );
  }
}
const mapStateToProps = ({ trekOverview, selectedTrek, lightboxState }) => ({
  renderedAsModal: trekOverview.isModalOpen,
  trekId: selectedTrek.trek.id,
  isLightBoxOpen: lightboxState.isOpen,
  terrain_information: selectedTrek.trek.terrain_information,
  quick_facts: selectedTrek.trek.quick_facts,
  elevation_profile: selectedTrek.trek.elevation_profile,
});

export default connect(
  mapStateToProps,
  {
    closeEditor,
    updateOverviewEdits,
    patchOverviewEdits,
    openLightbox,
    closeOverviewModal,
  }
)(TerrainInfoEditor);



// WEBPACK FOOTER //
// ./src/components/TrekDetail/Editors/TerrainInfo/TerrainInfoEditor.jsx